import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardActionArea, CardContent, Chip, Divider, MenuItem, Pagination, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getFeedback, updateFeedbackStatus } from '../services/FeedbackService';
import GameDashboard from './GameDashboard';
import { feedbackRecordSx, panelSx } from '../theme';
import useAdminPasswordConfirmation, { adminPasswordErrorKey } from '../hooks/useAdminPasswordConfirmation';

const statuses = ['New', 'InProgress', 'Solved', 'Unnecessary'];

export default function AdminDashboard() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { requestPassword, passwordDialog } = useAdminPasswordConfirmation();
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState('');
    const [draftStatus, setDraftStatus] = useState('');
    const [data, setData] = useState({ items: [], total: 0 });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [drafts, setDrafts] = useState({});
    const [saved, setSaved] = useState(false);
    const adminMenus = [
        { path: '/admin/subscriptions', label: t('subscription.manage') },
        { path: '/admin/subscription-settings', label: t('subscription.settings') },
        { path: '/admin/app-settings', label: t('subscription.appSettings') },
        { path: '/admin/announcements', label: t('announcement.manage') },
    ];
    const load = useCallback(async () => {
        setBusy(true); setError(''); setSaved(false);
        try { setData(await getFeedback(page, status)); }
        catch { setError(t('feedback.loadFailed')); }
        finally { setBusy(false); }
    }, [page, status, t]);
    useEffect(() => { load(); }, [load]);
    const changeStatus = async (id, value) => {
        const password = await requestPassword();
        if (password === null) return;
        setBusy(true); setError(''); setSaved(false);
        try {
            await updateFeedbackStatus(id, value, password);
            // Show the confirmed status even if reloading the list fails.
            setData(current => ({ ...current, items: current.items.map(item => item.id === id ? { ...item, status: value } : item) }));
            setDrafts(current => { const next = { ...current }; delete next[id]; return next; });
            await load(); setSaved(true);
        }
        catch (error) { setError(t(adminPasswordErrorKey(error) || 'feedback.updateFailed')); }
        finally { setBusy(false); }
    };
    return <main className="app-page dashboard-page" style={{ padding: '4.25rem 1rem 1rem' }}>
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            <Typography component="h1" variant="h5" sx={{ mb: 2 }}>{t('feedback.dashboard')}</Typography>
            <Box component="nav" aria-label={t('feedbackUi.adminMenu')} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25, mb: 3 }}>
                {adminMenus.map(menu => <Card key={menu.path}>
                    <CardActionArea onClick={() => navigate(menu.path)} sx={{ minHeight: 78, px: 2 }}>
                        <Typography component="span" sx={{ fontWeight: 800 }}>{menu.label}</Typography>
                    </CardActionArea>
                </Card>)}
            </Box>
            <Box component="section" aria-label={t('feedbackUi.rooms')} sx={panelSx}>
                <Typography component="h2" variant="h6" sx={{ mb: 2 }}>{t('feedbackUi.rooms')}</Typography>
                <GameDashboard embedded />
            </Box>
            <Box component="section" aria-label={t('feedback.title')} sx={panelSx}>
            <Typography component="h2" variant="h6" sx={{ mb: 2 }}>{t('feedback.title')}</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {saved && <Alert severity="success" sx={{ mb: 2 }}>{t('feedbackUi.saved')}</Alert>}
            <Box component="form" onSubmit={event => { event.preventDefault(); setStatus(draftStatus); setPage(1); }} sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
                <TextField select size="small" label={t('feedback.status')} value={draftStatus} disabled={busy}
                    onChange={event => setDraftStatus(event.target.value)} sx={{ minWidth: 180 }}>
                    {['','New','InProgress','Solved','Unnecessary'].map(value => <MenuItem key={value} value={value}>{t(`feedback.${value || 'all'}`)}</MenuItem>)}
                </TextField>
                <Button type="submit" variant="contained" disabled={busy}>{t('dashboardUi.applyFilters')}</Button>
                <Button type="button" variant="outlined" disabled={busy} onClick={load}>{t('feedback.refresh')}</Button>
            </Box>
            <Typography role="status">{busy ? t('common.loading') : t('feedback.total', {count: data.total})}</Typography>
            {!busy && data.items.length === 0 && <Typography color="text.secondary" sx={{ my: 2 }}>{t('feedbackUi.empty')}</Typography>}
            {data.items.map(item => <Card key={item.id} sx={feedbackRecordSx}><CardContent>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 2, rowGap: .25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: 'anywhere', minWidth: 0 }}>{item.senderName} · {item.senderEmail}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('feedback.sentAt')}: {new Date(item.createdAt).toLocaleString()}</Typography>
                </Box>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', my: 1 }}>{item.message}</Typography>
                <Divider sx={{ mb: 1.5 }} />
                <Box component="form" onSubmit={event => { event.preventDefault(); if (!busy && drafts[item.id] && drafts[item.id] !== item.status) changeStatus(item.id, drafts[item.id]); }}
                    sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: .75, mr: { sm: 'auto' } }}>
                        <Typography variant="caption">{t('feedbackUi.currentStatus')}:</Typography>
                        <Chip size="small" variant="outlined" label={t(`feedback.${item.status}`)} />
                    </Box>
                    <TextField select size="small" label={t('feedbackUi.changeStatus')} value={drafts[item.id] ?? item.status} disabled={busy}
                        onChange={event => setDrafts(current => ({ ...current, [item.id]: event.target.value }))} sx={{ minWidth: 150 }}>
                        {statuses.map(value => <MenuItem key={value} value={value}>{t(`feedback.${value}`)}</MenuItem>)}
                    </TextField>
                    <Button type="submit" variant="contained" disabled={busy || !drafts[item.id] || drafts[item.id] === item.status}>{t('feedbackUi.save')}</Button>
                </Box>
            </CardContent></Card>)}
            <Pagination count={Math.max(1, Math.ceil(data.total / 20))} page={page} disabled={busy} onChange={(_, value) => setPage(value)} />
            </Box>
            {passwordDialog}
        </Box>
    </main>;
}
